import {Button, RelativeTime, Truncate} from '@primer/react'
import {CommentIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {
  ReviewTimelineEvent,
  CommitTimelineEvent,
  LabeledTimelineEvent,
  ReviewRequestedTimelineEvent,
  IssueCommentTimelineEvent,
} from '../../routes/route-payload-types'
import {getEventVerb, formatTruncatedTeams, type TaggedEvent} from '../../helpers/catch-up-helpers'
import styles from './Activity.module.css'
import {Link} from '@github-ui/react-core/link'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {DockedPanel, useDockedPanelActions} from '../../contexts/DockedPanelContext'
import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'

interface EventListProps {
  events: TaggedEvent[]
}

export function EventList({events}: EventListProps) {
  const {urls} = useLayoutRouteData()
  const {changes: changesUrl, conversation: conversationUrl} = urls
  const dockedPanelActions = useDockedPanelActions()
  const shouldUseCommentsPanelForIssueComments = isFeatureEnabled('updated_pull_request_header')
  const shouldUseCommentsPanelForReviews =
    shouldUseCommentsPanelForIssueComments && isFeatureEnabled('prs_docked_comments_replies')

  return (
    <ul className={styles.eventList}>
      {events.map((taggedEvent, index) => {
        const {type: eventType, event} = taggedEvent
        const eventId = `${eventType}-${index}`

        let eventVerb = getEventVerb(eventType)
        let eventText = ''

        if (eventType === 'labeled' || eventType === 'unlabeled') {
          const labeledEvent = event as LabeledTimelineEvent
          const isUnlabeled = eventType === 'unlabeled'
          const labelName = labeledEvent.additionalInfo.labelName

          if (labelName) {
            eventText = isUnlabeled ? `Removed label "${labelName}" ` : `Added label "${labelName}" `
          } else {
            eventVerb = isUnlabeled ? 'unlabeled' : 'labeled'
          }
        } else if (eventType === 'review-requested' || eventType === 'review-request-removed') {
          const reviewRequestedEvent = event as ReviewRequestedTimelineEvent
          const isRemoval = eventType === 'review-request-removed'
          const requestedReviewer = reviewRequestedEvent.additionalInfo.requestedReviewer

          if (requestedReviewer) {
            eventText = isRemoval
              ? `Removed review request for ${requestedReviewer} `
              : `Requested review from ${requestedReviewer} `
          } else {
            eventVerb = isRemoval ? 'removed review request' : 'requested review'
          }
        }

        // For commits, add a button link to individual commit
        if (eventType === 'commit') {
          const commitEvent = event as CommitTimelineEvent
          const commitUrl = `${changesUrl}/${commitEvent.additionalInfo.sha}`
          const shortSha = commitEvent.additionalInfo.sha.substring(0, 7)
          const shortMessage = commitEvent.additionalInfo.shortMessageText
          return (
            <li key={eventId} className={styles.event}>
              <span className={styles.eventText}>
                <span>
                  Commit <code className={styles.eventSha}>{shortSha}</code> by
                </span>
                <GitHubAvatar src={event.user.avatarUrl} size={20} />
                <strong>{event.user.login}</strong>
              </span>
              {shortMessage && (
                <Truncate inline title={shortMessage}>
                  {shortMessage}
                </Truncate>
              )}
              <span className={styles.eventTimestamp}>
                <RelativeTime date={new Date(event.timestamp)} />
              </span>
              <Button
                as={Link}
                to={commitUrl}
                size="small"
                variant="invisible"
                aria-label={`View commit ${shortSha}`}
                className={styles.eventButton}
              >
                View commit
              </Button>
            </li>
          )
        }

        // For reviews (approval, changes-requested, review), add appropriate buttons
        if (eventType === 'approval' || eventType === 'changes-requested' || eventType === 'review') {
          const reviewEvent = event as ReviewTimelineEvent

          // For review events
          const reviewId = reviewEvent.additionalInfo.reviewId
          const commentCount = reviewEvent.additionalInfo.commentCount
          const reviewUrl = `${conversationUrl}#pullrequestreview-${reviewId}`
          const teams = reviewEvent.additionalInfo.requestedReviewerTeams
          const reviewedCommitOid = reviewEvent.additionalInfo.reviewedCommitOid

          // Build the review text: "{author} {verb}"
          const reviewText = `${event.user.login} ${eventVerb}`

          // Build context parts
          const contextParts: string[] = []
          if (reviewedCommitOid) {
            const shortOid = reviewedCommitOid.substring(0, 7)
            if (eventType === 'changes-requested') {
              contextParts.push(`for commit ${shortOid}`)
            } else {
              contextParts.push(`commit ${shortOid}`)
            }
          }
          if (teams && teams.length > 0) {
            const teamNames = formatTruncatedTeams(teams)
            contextParts.push(`on behalf of ${teamNames}`)
          }

          return (
            <li key={eventId} className={styles.event}>
              <span className={styles.eventText}>
                <GitHubAvatar src={event.user.avatarUrl} size={20} />
                <span>{reviewText}</span>
                {contextParts.length > 0 && <span className={styles.eventContext}>{contextParts.join(' ')}</span>}
              </span>
              <span className={styles.eventTimestamp}>
                <RelativeTime date={new Date(event.timestamp)} />
              </span>
              {commentCount > 0 ? (
                shouldUseCommentsPanelForReviews ? (
                  <Button
                    onClick={() =>
                      dockedPanelActions.openPanel(DockedPanel.Comments, {
                        metadata: {filterText: `review-id:${reviewId}`, viewMode: 'review-comments'},
                      })
                    }
                    size="small"
                    variant="invisible"
                    aria-label="View review threads"
                    leadingVisual={CommentIcon}
                    count={commentCount}
                    className={styles.eventButton}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    to={reviewUrl}
                    size="small"
                    variant="invisible"
                    aria-label="View review threads"
                    leadingVisual={CommentIcon}
                    count={commentCount}
                    className={styles.eventButton}
                  >
                    View
                  </Button>
                )
              ) : null}
            </li>
          )
        }

        // For issue comments, add a button to open the comments panel
        if (eventType === 'issue-comment') {
          const issueCommentEvent = event as IssueCommentTimelineEvent
          const commentId = issueCommentEvent.additionalInfo.commentId
          const commentUrl = `${conversationUrl}#issuecomment-${commentId}`

          return (
            <li key={eventId} className={styles.event}>
              <span className={styles.eventText}>
                <GitHubAvatar src={event.user.avatarUrl} size={20} />
                <strong>{event.user.login}</strong>
                <span>left a comment on this PR</span>
              </span>
              <span className={styles.eventTimestamp}>
                <RelativeTime date={new Date(event.timestamp)} />
              </span>
              {commentId ? (
                shouldUseCommentsPanelForIssueComments ? (
                  <Button
                    onClick={() =>
                      dockedPanelActions.openPanel(DockedPanel.Comments, {
                        metadata: {viewMode: 'issue-comments', filterText: `id:${commentId}`},
                      })
                    }
                    size="small"
                    variant="invisible"
                    aria-label="View comments"
                    leadingVisual={CommentIcon}
                    className={styles.eventButton}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    to={commentUrl}
                    size="small"
                    variant="invisible"
                    aria-label="View comments"
                    leadingVisual={CommentIcon}
                    className={styles.eventButton}
                  >
                    View
                  </Button>
                )
              ) : null}
            </li>
          )
        }

        return (
          <li key={eventId} className={styles.event}>
            <span className={styles.eventText}>
              <span>
                {eventText ? `${eventText}by` : `${eventVerb.charAt(0).toUpperCase() + eventVerb.slice(1)} by`}
              </span>
              <GitHubAvatar src={event.user.avatarUrl} size={20} />
              <strong>{event.user.login}</strong>
            </span>
            <span className={styles.eventTimestamp}>
              <RelativeTime date={new Date(event.timestamp)} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}
